import Container from "../../../components/element/container";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Button from "../../../components/element/button";
import Col from "../../../components/element/col";
import Icon from "../../../components/element/icon";
import Image from "../../../components/element/image";
import Row from "../../../components/element/row";
import RangeSlider from "react-range-slider-input";
import {
  setFilterData,
  setSavedProduct,
} from "../../../redux/reducer/product.reducer";
import {
  getBrandList,
  getModelList,
} from "../../../redux/reducer/common.reducer";
import {
  Convertible,
  Coupe,
  Hatchback,
  MPV,
  Sedan,
  SUV,
  Truck,
  Wagon,
} from "../../../assets/images";

const filterList = ["Brand Model", "Price", "Year", "Body Type"];
const bodyTypeList = [
  { label: "Convertible", img: Convertible },
  { label: "Coupe", img: Coupe },
  { label: "Hatchback", img: Hatchback },
  { label: "MPV", img: MPV },
  { label: "Sedan", img: Sedan },
  { label: "SUV", img: SUV },
  { label: "Truck", img: Truck },
  { label: "Wagon", img: Wagon },
];

const sortList = [
  { label: "Default", value: "" },
  { label: "Price (Highest)", value: "desc" },
  { label: "Price (Lowest)", value: "asc" },
];

export default function HomeSidebar() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [openDropdown, setOpenDropdown] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({});
  const { categoryList, filterData, productList } = useSelector(
    (state) => state.product,
  );
  const [draftFilterData, setDraftFilterData] = useState(filterData);
  const [isDraftDirty, setIsDraftDirty] = useState(false);
  const [isCategoryDirty, setIsCategoryDirty] = useState(false);
  const { brandList, modelOption } = useSelector((state) => state.common);

  const category = draftFilterData.category || "all";
  const categoryData = draftFilterData[category] || {};
  const currentYear = new Date().getFullYear();
  const defaultRanges = {
    price: [100000, 1000000],
    year: [2016, currentYear],
  };
  const normalizeOptions = (options) =>
    (Array.isArray(options) ? options : Object.values(options || {}).flat())
      .map((item) => ({
        ...item,
        id: item.id,
        label: item.label || item.name,
      }))
      .filter((item) => item.id != null && item.label);
  const brandOptions = normalizeOptions(brandList);
  const modelOptions = normalizeOptions(modelOption);
  const locationList = Array.from(
    new Set((productList?.location || []).map((item) => item)),
  ).filter(Boolean);

  const isRangeActive = (name) => {
    const range = categoryData[name];
    const defaults = defaultRanges[name];

    return (
      Array.isArray(range) &&
      range.length === 2 &&
      (Number(range[0]) !== defaults[0] || Number(range[1]) !== defaults[1])
    );
  };

  const filterState = {
    "Brand Model": {
      active:
        (categoryData.brand || []).length > 0 ||
        (categoryData.car_model || []).length > 0,
      count:
        (categoryData.brand || []).length +
        (categoryData.car_model || []).length,
    },
    Price: {
      active: isRangeActive("price"),
      count: isRangeActive("price") ? 1 : 0,
    },
    Year: {
      active: isRangeActive("year"),
      count: isRangeActive("year") ? 1 : 0,
    },
    "Body Type": {
      active: (categoryData.body_type || []).length > 0,
      count: (categoryData.body_type || []).length,
    },
  };

  useEffect(() => {
    if (brandList.length === 0) dispatch(getBrandList());
    if (Object.keys(modelOption || {}).length === 0) dispatch(getModelList());
  }, [brandList.length, dispatch, modelOption]);

  useEffect(() => {
    setDraftFilterData((current) => {
      if (!isDraftDirty) return filterData;
      // An unapplied category is kept, so the draft still follows the category
      // tiles on the page until the sidebar's own category is touched.
      if (isCategoryDirty) return current;

      return { ...current, category: filterData.category || "all" };
    });
  }, [filterData, isDraftDirty, isCategoryDirty]);

  useEffect(() => {
    const closeDropdown = (event) => {
      if (!(event.target instanceof Element)) return;

      if (
        !event.target.closest(".sidebar-filter-dropdown") &&
        !event.target.closest(".sidebar-filter-button")
      ) {
        setOpenDropdown("");
      }
    };

    document.addEventListener("pointerdown", closeDropdown, true);
    return () =>
      document.removeEventListener("pointerdown", closeDropdown, true);
  }, []);

  useEffect(() => {
    const closeOpenDropdown = () => setOpenDropdown("");

    window.addEventListener("scroll", closeOpenDropdown, { passive: true });
    window.addEventListener("resize", closeOpenDropdown);

    return () => {
      window.removeEventListener("scroll", closeOpenDropdown);
      window.removeEventListener("resize", closeOpenDropdown);
    };
  }, []);

  const updateCategoryFilter = (updates) => {
    setIsDraftDirty(true);
    setDraftFilterData((currentFilterData) => ({
      ...currentFilterData,
      [category]: {
        ...(currentFilterData[category] || {}),
        ...updates,
        start: 0,
      },
    }));
  };

  const toggleFilterValue = (name, value) => {
    const values = categoryData[name] || [];

    updateCategoryFilter({
      [name]: values.includes(value)
        ? values.filter((item) => item !== value)
        : [...values, value],
    });
  };

  const updateRange = (name, index, value) => {
    const range = [...(categoryData[name] || defaultRanges[name])];
    range[index] = Number(value) || defaultRanges[name][index];
    updateCategoryFilter({ [name]: range });
  };

  const handleDropdownToggle = (filter, event) => {
    if (openDropdown === filter) {
      setOpenDropdown("");
      return;
    }

    const buttonRect = event.currentTarget.getBoundingClientRect();
    const dropdownWidth =
      filter === "Brand Model" || filter === "Body Type" ? 430 : 320;
    const availableHeight = window.innerHeight - 32;
    const dropdownHeight = Math.min(
      filter === "Brand Model" || filter === "Body Type" ? 560 : 320,
      availableHeight,
    );

    setDropdownPosition({
      top: Math.max(
        16,
        Math.min(buttonRect.top, window.innerHeight - dropdownHeight - 16),
      ),
      left: Math.min(
        buttonRect.right + 12,
        window.innerWidth - dropdownWidth - 16,
      ),
      width: dropdownWidth,
      maxHeight: availableHeight,
    });
    setOpenDropdown(filter);
  };

  const updateCategory = (value) => {
    const selectedCategory = value || "all";
    const nextCategory =
      selectedCategory === category ? "all" : selectedCategory;

    // The category is held in the draft like every other filter, so the product
    // list only changes once Apply Filter is pressed.
    setOpenDropdown("");
    setIsDraftDirty(true);
    setIsCategoryDirty(true);
    setDraftFilterData((currentFilterData) => ({
      ...currentFilterData,
      category: nextCategory,
    }));
  };

  const updateLocation = (location) => {
    updateCategoryFilter({ location: location ? [location] : [] });
  };

  const updateSort = (sort) => {
    updateCategoryFilter({ sort });
  };

  const applyFilter = () => {
    setOpenDropdown("");
    setIsDraftDirty(false);
    setIsCategoryDirty(false);
    dispatch(setSavedProduct([]));
    dispatch(
      setFilterData({
        ...draftFilterData,
        category,
        [category]: {
          ...categoryData,
          start: 0,
          refresh_key: Date.now(),
        },
      }),
    );
  };

  const resetFilter = () => {
    const clearedFilterData = {
      category: "all",
      all: {
        start: 0,
        refresh_key: Date.now(),
      },
    };

    setOpenDropdown("");
    setIsDraftDirty(false);
    setIsCategoryDirty(false);
    setDraftFilterData(clearedFilterData);
    dispatch(setSavedProduct([]));
    dispatch(setFilterData(clearedFilterData));
  };

  return (
    <aside
      className={`filter-sidebar ${openDropdown ? "has-open-filter" : ""}`}
    >
      <Container tag="div" className="sidebar-scroll-content">
        <Row className="_row sidebar-heading">
          <h2 className="_label sidebar-title">{t("Filter Cars")}</h2>
          <Button className="_button sidebar-clear" onClick={resetFilter}>
            {t("Clear All")}
          </Button>
        </Row>

        <Col className="_col sidebar-filter-list">
          {filterList.map((filter) => {
            const state = filterState[filter];
            const isOpen = openDropdown === filter;

            return (
              <Container tag="div" className="sidebar-filter-control" key={filter}>
                <Button
                  className={`_button sidebar-filter-button ${
                    state.active ? "active" : ""
                  } ${isOpen ? "open" : ""}`}
                  onClick={(event) => handleDropdownToggle(filter, event)}
                  aria-expanded={isOpen}
                >
                  <span className="_label sidebar-filter-label">
                    {t(filter === "Body Type" ? "Body" : filter)}
                  </span>
                  <span className="sidebar-filter-indicator">
                    {/* {state.count > 0 && (
                      <span className="_label sidebar-filter-count">
                        {state.count}
                      </span>
                    )} */}
                    <Icon
                      icon="iconamoon:arrow-down-2"
                      className="_svg sidebar-chevron"
                    />
                  </span>
                </Button>
                {isOpen && (
                  <DesktopFilterDropdown
                    type={filter}
                    expanded={isOpen}
                    style={dropdownPosition}
                    categoryData={categoryData}
                    brands={brandOptions}
                    models={modelOptions}
                    defaultRanges={defaultRanges}
                    onToggle={toggleFilterValue}
                    onUpdate={updateCategoryFilter}
                    onRangeChange={updateRange}
                    t={t}
                  />
                )}
              </Container>
            );
          })}
        </Col>

        <SidebarSection
          title="Category"
          icon="streamline-ultimate:car-3-bold"
          width={15.5}
          height={15.5}
          isGrid
        >
          {categoryList.map((item, index) => (
            <SidebarOption
              key={item.value}
              active={
                category === item.value || (category === "all" && index === 0)
              }
              label={t(item.label)}
              onClick={() => updateCategory(item.value)}
            />
          ))}
        </SidebarSection>

        <SidebarSection
          icon="entypo:location"
          title="Location"
          width={15}
          height={15}
          collapsible
        >
          <SidebarOption
            active={!categoryData.location?.length}
            label={t("All Location")}
            onClick={() => updateLocation("")}
          />
          {locationList.slice(0, 3).map((location) => (
            <SidebarOption
              key={location}
              active={categoryData.location?.includes(location)}
              label={location}
              onClick={() => updateLocation(location)}
            />
          ))}
          {locationList.length === 0 && (
            <SidebarOption
              label={t("Selangor")}
              active={categoryData.location?.includes("Selangor")}
              onClick={() => updateLocation("Selangor")}
            />
          )}
        </SidebarSection>

        <SidebarSection
          icon="mynaui:sort-solid"
          title="Sort"
          width={19.5}
          height={19.5}
          collapsible
        >
          {sortList.map((item) => (
            <SidebarOption
              key={item.label}
              active={(categoryData.sort || "") === item.value}
              label={t(item.label)}
              onClick={() => updateSort(item.value)}
            />
          ))}
        </SidebarSection>
      </Container>

      <Col className="_col sidebar-actions">
        <Button className="_button sidebar-apply" onClick={applyFilter}>
          {t("Apply Filter")}
        </Button>
      </Col>
    </aside>
  );
}

function DesktopFilterDropdown({
  type,
  expanded = false,
  style,
  categoryData,
  brands,
  models,
  defaultRanges,
  onToggle,
  onUpdate,
  onRangeChange,
  t,
}) {
  const [expandedBrands, setExpandedBrands] = useState([]);

  const toggleBrandAccordion = (brandId) => {
    setExpandedBrands((current) =>
      current.includes(brandId)
        ? current.filter((id) => id !== brandId)
        : [...current, brandId],
    );
  };

  const toggleBrand = (brand, brandModels) => {
    const selectedBrands = categoryData.brand || [];
    const selectedModels = categoryData.car_model || [];
    const brandModelIds = brandModels.map((model) => model.id);
    const selectingBrand = !selectedBrands.includes(brand.id);

    onUpdate({
      brand: selectingBrand
        ? [...selectedBrands, brand.id]
        : selectedBrands.filter((id) => id !== brand.id),
      car_model: selectingBrand
        ? selectedModels.filter((id) => !brandModelIds.includes(id))
        : selectedModels,
    });
  };

  const toggleModel = (brand, brandModels, modelId) => {
    const selectedBrands = categoryData.brand || [];
    const selectedModels = categoryData.car_model || [];
    const brandModelIds = brandModels.map((model) => model.id);

    if (selectedBrands.includes(brand.id)) {
      onUpdate({
        brand: selectedBrands.filter((id) => id !== brand.id),
        car_model: Array.from(
          new Set([
            ...selectedModels.filter((id) => !brandModelIds.includes(id)),
            ...brandModelIds.filter((id) => id !== modelId),
          ]),
        ),
      });
      return;
    }

    let nextModels = selectedModels.includes(modelId)
      ? selectedModels.filter((id) => id !== modelId)
      : [...selectedModels, modelId];
    let nextBrands = selectedBrands;

    if (
      brandModelIds.length > 0 &&
      brandModelIds.every((id) => nextModels.includes(id))
    ) {
      nextModels = nextModels.filter((id) => !brandModelIds.includes(id));
      nextBrands = Array.from(new Set([...selectedBrands, brand.id]));
    }

    onUpdate({
      brand: nextBrands,
      car_model: nextModels,
    });
  };

  const clearFilter = () => {
    if (type === "Brand Model") {
      onUpdate({ brand: [], car_model: [] });
      return;
    }

    if (type === "Body Type") {
      onUpdate({ body_type: [] });
      return;
    }

    onUpdate({ [type.toLowerCase()]: defaultRanges[type.toLowerCase()] });
  };

  const renderFooter = () => (
    <Row className="_row sidebar-dropdown-footer">
      <button
        type="button"
        className="_button sidebar-dropdown-clear"
        onClick={clearFilter}
      >
        {t("Reset")}
      </button>
    </Row>
  );

  if (type === "Price" || type === "Year") {
    const name = type.toLowerCase();
    const range = categoryData[name] || defaultRanges[name];

    return (
      <section
        className={`sidebar-filter-dropdown range-dropdown ${
          expanded ? "expanded" : ""
        }`}
        style={style}
      >
        <Container tag="div" className="sidebar-dropdown-content">
          <RangeSlider
            min={type === "Year" ? 2000 : 50000}
            max={type === "Year" ? defaultRanges.year[1] : 5000000}
            step={type === "Year" ? 1 : 50000}
            value={range}
            onInput={(value) => onUpdate({ [name]: value })}
          />
          <Row className="_row sidebar-range-inputs">
            <label>
              <span className="_label sidebar-input-label">
                {t("Min.")} {t(type)}
              </span>
              <input
                type="number"
                min={type === "Year" ? 2000 : 0}
                max={type === "Year" ? defaultRanges.year[1] : undefined}
                step={type === "Year" ? 1 : 50000}
                value={range[0]}
                onChange={(event) => onRangeChange(name, 0, event.target.value)}
              />
            </label>
            <span className="sidebar-range-divider">-</span>
            <label>
              <span className="_label sidebar-input-label">
                {t("Max.")} {t(type)}
              </span>
              <input
                type="number"
                min={type === "Year" ? 2000 : 0}
                max={type === "Year" ? defaultRanges.year[1] : undefined}
                step={type === "Year" ? 1 : 50000}
                value={range[1]}
                onChange={(event) => onRangeChange(name, 1, event.target.value)}
              />
            </label>
          </Row>
        </Container>
        {renderFooter()}
      </section>
    );
  }

  if (type === "Brand Model") {
    return (
      <section
        className={`sidebar-filter-dropdown brand-model-dropdown ${
          expanded ? "expanded" : ""
        }`}
        style={style}
      >
        <Container tag="div" className="sidebar-dropdown-content">
          <Container tag="div" className="sidebar-brand-list">
            {brands
              .slice()
              .sort((first, second) => first.label.localeCompare(second.label))
              .map((brand) => {
                const brandModels = models
                  .filter((model) => model.brand_id === brand.id)
                  .sort((first, second) =>
                    first.label.localeCompare(second.label, undefined, {
                      numeric: true,
                    }),
                  );
                const isBrandSelected = (categoryData.brand || []).includes(
                  brand.id,
                );
                const isExpanded = expandedBrands.includes(brand.id);

                return (
                  <Container tag="div" className="sidebar-brand-item" key={brand.id}>
                    <Row
                      className="_row sidebar-brand-row"
                      onClick={() => toggleBrandAccordion(brand.id)}
                    >
                      <article
                        className="checkbox-wrapper sidebar-brand-checkbox"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <label>
                          <input
                            className="pointer"
                            type="checkbox"
                            checked={isBrandSelected}
                            onChange={() => toggleBrand(brand, brandModels)}
                          />
                          <span className="checkbox" />
                        </label>
                      </article>
                      <span className="_label sidebar-brand-title">
                        {t(brand.label)}
                      </span>
                      <Icon
                        icon="icon-park-outline:down"
                        className={`_svg sidebar-brand-chevron ${
                          isExpanded ? "open" : ""
                        }`}
                      />
                    </Row>
                    {isExpanded && brandModels.length > 0 && (
                      <Row className="_row sidebar-model-list">
                        {brandModels.map((model) => (
                          <button
                            type="button"
                            className={`_button sidebar-model-item ${
                              isBrandSelected ||
                              (categoryData.car_model || []).includes(model.id)
                                ? "active"
                                : ""
                            }`}
                            key={model.id}
                            onClick={() =>
                              toggleModel(brand, brandModels, model.id)
                            }
                          >
                            <span className="_label sidebar-model-title">
                              {t(model.label)}
                            </span>
                          </button>
                        ))}
                      </Row>
                    )}
                  </Container>
                );
              })}
          </Container>
        </Container>
        {renderFooter()}
      </section>
    );
  }

  return (
    <section
      className={`sidebar-filter-dropdown body-type-dropdown ${
        expanded ? "expanded" : ""
      }`}
      style={style}
    >
      <Container tag="div" className="sidebar-dropdown-content">
        <Container tag="div" className="sidebar-body-list">
          {bodyTypeList.map((item) => {
            const active = (categoryData.body_type || []).includes(item.label);

            return (
              <Row
                className="_row sidebar-body-row"
                key={item.label}
                onClick={() => onToggle("body_type", item.label)}
              >
                <article
                  className="checkbox-wrapper sidebar-body-checkbox"
                  onClick={(event) => event.stopPropagation()}
                >
                  <label>
                    <input
                      className="pointer"
                      type="checkbox"
                      checked={active}
                      onChange={() => onToggle("body_type", item.label)}
                    />
                    <span className="checkbox" />
                  </label>
                </article>
                <Image
                  src={item.img}
                  alt=""
                  className="_img sidebar-body-image"
                />
                <span className="_label sidebar-body-title">
                  {t(item.label)}
                </span>
              </Row>
            );
          })}
        </Container>
      </Container>
      {renderFooter()}
    </section>
  );
}

function SidebarSection({
  children,
  title,
  icon,
  width,
  height,
  isGrid,
  collapsible = false,
  defaultExpanded = true,
}) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const sectionHeading = (
    <>
      <Icon
        tag="span"
        icon={icon}
        className="_svg sidebar-section-icon"
        gradient
        width={width || 18}
        height={height || 18}
      />
      <h3 className="_label sidebar-section-title">{t(title)}</h3>
    </>
  );

  return (
    <section
      className={`sidebar-section ${
        collapsible && !isExpanded ? "collapsed" : ""
      }`}
    >
      {collapsible ? (
        <Button
          type="button"
          className="_button sidebar-section-toggle"
          onClick={() => setIsExpanded((expanded) => !expanded)}
          aria-expanded={isExpanded}
        >
          {sectionHeading}
          <Icon
            icon="iconamoon:arrow-down-2"
            className={`_svg sidebar-section-chevron ${
              isExpanded ? "expanded" : ""
            }`}
          />
        </Button>
      ) : (
        <Row className="_row sidebar-section-heading">{sectionHeading}</Row>
      )}
      {(!collapsible || isExpanded) && (
        <Col className={`_col sidebar-option-list`}>{children}</Col>
      )}
    </section>
  );
}

function SidebarOption({ active = false, label, onClick }) {
  return (
    <button
      type="button"
      className={`_button sidebar-option ${active ? "active" : ""}`}
      onClick={onClick}
    >
      <span className="sidebar-radio" />
      <span className="_label sidebar-option-label">{label}</span>
    </button>
  );
}
